/**
 * PersistentInsights - Keeps Insights iframe mounted across navigation
 *
 * Follows PersistentChatwoot pattern:
 * - Lazy mount: only renders after user first visits /insights
 * - visibility:hidden when navigating away (keeps connections alive)
 * - Shown instantly when returning to /insights
 *
 * URL sync: two-way browser URL ↔ iframe path synchronization
 * - Iframe ROUTE_CHANGE → updates browser URL (push for significant, replace for minor)
 * - Browser back/forward → sends NAVIGATE to iframe
 * - Loop prevention via parentInitiated flag + lastSyncedPath comparison
 */

import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { InsightsEmbed } from "@/components/InsightsEmbed";
import type { InsightsEmbedRef } from "@/components/InsightsEmbed/types";
import { useAuthStore } from "@/lib/store/authStore";
import { isSignificantNavigation } from "./urlSyncUtils";

export function PersistentInsights() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOnInsightsPage = location.pathname.startsWith("/insights");

  // Lazy mount: only render after first visit
  const [hasVisitedInsights, setHasVisitedInsights] = useState(false);

  // URL sync refs — loop prevention + deep link initialPath
  const lastSyncedPath = useRef('');
  const initialPathRef = useRef<string | undefined>(undefined);
  const parentInitiated = useRef(false);
  const insightsRef = useRef<InsightsEmbedRef>(null);

  const isProvisioned = useAuthStore((state) => state.isProvisioned);

  useEffect(() => {
    if (isOnInsightsPage && !hasVisitedInsights) {
      // Extract sub-path: "/insights/dashboard/X" → "/dashboard/X", "/insights" → ""
      const subPath = location.pathname.slice(9); // Remove "/insights"
      initialPathRef.current = subPath || undefined; // undefined lets embed use its default (/dashboards)
      lastSyncedPath.current = subPath;
      setHasVisitedInsights(true);
    }
  }, [isOnInsightsPage, hasVisitedInsights, location.pathname]);

  // Restore last-viewed URL when returning to Insights from another app (sidebar click)
  useEffect(() => {
    if (parentInitiated.current) return; // Back-button effect handles this
    if (isOnInsightsPage && hasVisitedInsights && lastSyncedPath.current) {
      const expectedPath = `/insights${lastSyncedPath.current}`;
      if (location.pathname !== expectedPath) {
        navigate(expectedPath, { replace: true });
      }
    }
  }, [isOnInsightsPage, hasVisitedInsights, navigate, location.pathname]);

  // Back/forward button: when location changes while on Insights page,
  // send NAVIGATE to iframe if path differs from lastSyncedPath
  useEffect(() => {
    if (!isOnInsightsPage || !hasVisitedInsights) return;
    const subPath = location.pathname.slice(9); // Remove "/insights"
    if (subPath && subPath !== lastSyncedPath.current) {
      if (!insightsRef.current?.isReady) return;
      parentInitiated.current = true;
      lastSyncedPath.current = subPath;
      insightsRef.current?.navigate(subPath);
      // Safety: reset parentInitiated if no ROUTE_CHANGE echo within 500ms
      const safetyTimer = setTimeout(() => {
        parentInitiated.current = false;
      }, 500);
      return () => clearTimeout(safetyTimer);
    }
  }, [location.pathname, isOnInsightsPage, hasVisitedInsights]);

  const handleReady = useCallback(() => {
    console.log("[PersistentInsights] Ready");
  }, []);

  const handleRouteChange = useCallback((path: string, _title: string) => {
    // Insights sends paths relative to /insights base (Vue Router, base '/insights')
    if (parentInitiated.current) {
      parentInitiated.current = false;
      return; // Skip — we initiated this navigation via back/forward
    }
    if (lastSyncedPath.current === path) return; // Skip duplicate
    const prevPath = lastSyncedPath.current;
    lastSyncedPath.current = path;
    if (isOnInsightsPage) {
      const shouldPush = isSignificantNavigation(prevPath, path);
      navigate(`/insights${path}`, { replace: !shouldPush });
    }
  }, [navigate, isOnInsightsPage]);

  const handleError = useCallback((message: string) => {
    console.error("[PersistentInsights] Error:", message);
  }, []);

  const handleNavigateParent = useCallback(
    (action: string) => {
      switch (action) {
        case "back":
          navigate(-1);
          break;
        case "home":
          navigate("/");
          break;
        default:
          if (action.startsWith("/")) {
            navigate(action);
          } else {
            console.warn("[PersistentInsights] Unknown navigate action:", action);
          }
      }
    },
    [navigate]
  );

  // Don't render if never visited or not provisioned
  if (!hasVisitedInsights || !isProvisioned) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 top-0 z-10 md:left-[72px] md:right-[48px]"
      style={{
        visibility: isOnInsightsPage ? "visible" : "hidden",
        pointerEvents: isOnInsightsPage ? "auto" : "none",
      }}
    >
      <InsightsEmbed
        ref={insightsRef}
        initialPath={initialPathRef.current}
        className="h-full w-full"
        onReady={handleReady}
        onRouteChange={handleRouteChange}
        onError={handleError}
        onNavigateParent={handleNavigateParent}
      />
    </div>
  );
}
